import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { BhojanSchemeFormFields } from "@project/admin/components/bhojan-scheme-form-fields";
import type { Field } from "@project/admin/components/resource-table";
import { bhojanSchemeService } from "@project/services/admin/bhojan-scheme.service";
import {
  priceToSumOfRupees,
  toBhojanFeatures,
  toBhojanPrice,
  type BhojanScheme,
  type BhojanSchemePayload,
} from "@project/types/bhojan";

const tableFields: Field[] = [
  {
    name: "badge_text",
    label: "Badge Text",
    showInTable: true,
    hidden: true,
  },
  {
    name: "title",
    label: "Title",
    showInTable: true,
    hidden: true,
  },
  {
    name: "technical_name",
    label: "Technical Name",
    showInTable: true,
    hidden: true,
  },
  {
    name: "price",
    label: "Price (₹)",
    type: "number",
    showInTable: true,
    currency: true,
    hidden: true,
  },
  {
    name: "features",
    label: "Features",
    type: "tags",
    showInTable: true,
    hidden: true,
  },
  {
    name: "display_order",
    label: "Display Order",
    type: "number",
    showInTable: true,
    hidden: true,
  },
  {
    name: "is_highlighted",
    label: "Highlighted",
    type: "boolean",
    showInTable: true,
    hidden: true,
  },
  {
    name: "is_active",
    label: "Active",
    type: "boolean",
    showInTable: true,
    hidden: true,
  },
];

function toPayload(payload: Record<string, unknown>): BhojanSchemePayload {
  const features = toBhojanFeatures(payload.features);
  if (!features.length) {
    throw new Error("At least one feature is required.");
  }

  const price = toBhojanPrice(payload.price);
  const sumOfRupees =
    String(payload.sum_of_rupees ?? "").trim() || priceToSumOfRupees(price);

  if (!sumOfRupees) {
    throw new Error("Sum of Rupees is required.");
  }

  return {
    badge_text: String(payload.badge_text ?? "").trim(),
    title: String(payload.title ?? "").trim(),
    technical_name: String(payload.technical_name ?? "").trim(),
    price,
    sum_of_rupees: sumOfRupees,
    features,
    display_order:
      payload.display_order !== undefined && payload.display_order !== ""
        ? Number(payload.display_order)
        : 0,
    is_highlighted:
      payload.is_highlighted === true || payload.is_highlighted === "true",
    is_active: payload.is_active === true || payload.is_active === "true",
  };
}

export default function Page() {
  return (
    <AdminShell
      title="Bhojan Schemes"
      subtitle="Manage multi-year meal sponsorship schemes shown on the public site"
    >
      <ApiResourceTable<BhojanScheme>
        title="Scheme"
        labelField="title"
        fields={tableFields}
        searchFields={["title", "technical_name", "badge_text"]}
        defaultValues={{
          is_active: true,
          is_highlighted: false,
          display_order: 1,
          features: [],
          sum_of_rupees: "",
        }}
        statusFilterOptions={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
        csvFileName="bhojan-schemes"
        dialogClassName="max-w-2xl"
        mapRecordToForm={record => ({
          ...record,
          sum_of_rupees:
            record.sum_of_rupees || priceToSumOfRupees(record.price),
          features: Array.isArray(record.features) ? record.features : [],
        })}
        buildPayload={editing => toPayload(editing as Record<string, unknown>)}
        renderForm={({ editing, setEditing }) => (
          <BhojanSchemeFormFields
            editing={editing as Record<string, unknown> | null}
            setEditing={value =>
              setEditing(value as Partial<BhojanScheme> | null)
            }
          />
        )}
        list={query => bhojanSchemeService.listPage(query)}
        create={payload =>
          bhojanSchemeService.create(payload as BhojanSchemePayload)
        }
        update={(id, payload) =>
          bhojanSchemeService.update(
            id,
            payload as Partial<BhojanSchemePayload>
          )
        }
        remove={id => bhojanSchemeService.delete(id)}
      />
    </AdminShell>
  );
}
